type Listener<T> = (value: T) => void;

export class Signal<T> {
  private _value: T;
  private _listeners: Set<Listener<T>> = new Set();

  constructor(initialValue: T) {
    this._value = initialValue;
  }

  get value(): T {
    return this._value;
  }

  set value(newValue: T) {
    if (this._value !== newValue) {
      this._value = newValue;
      this._listeners.forEach((listener) => listener(newValue));
    }
  }

  subscribe(listener: Listener<T>): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  update(updater: (current: T) => T): void {
    this.value = updater(this._value);
  }
}

export class ComputedSignal<T> {
  private _value: T;
  private _listeners: Set<Listener<T>> = new Set();
  private _unsubscribers: (() => void)[] = [];

  constructor(private _compute: () => T, dependencies: Signal<any>[]) {
    this._value = this._compute();

    // Subscribe to all dependencies
    dependencies.forEach((dep) => {
      const unsub = dep.subscribe(() => {
        const newValue = this._compute();
        if (this._value !== newValue) {
          this._value = newValue;
          this._listeners.forEach((listener) => listener(newValue));
        }
      });
      this._unsubscribers.push(unsub);
    });
  }

  get value(): T {
    return this._value;
  }

  subscribe(listener: Listener<T>): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  destroy(): void {
    this._unsubscribers.forEach((unsub) => unsub());
    this._listeners.clear();
  }
}

// Helper functions for common use cases
export function signal<T>(initialValue: T): Signal<T> {
  return new Signal(initialValue);
}

export function computed<T>(compute: () => T, dependencies: Signal<any>[]): ComputedSignal<T> {
  return new ComputedSignal(compute, dependencies);
}

// Effect function for side effects
export function effect<T>(fn: () => void, dependencies: Signal<any>[]): () => void {
  const unsubscribers = dependencies.map((dep) => dep.subscribe(fn));

  // Run the effect immediately
  fn();

  // Return cleanup function
  return () => unsubscribers.forEach((unsub) => unsub());
}
